/**
 * `MediaQueryList.addEventListener` shim for Safari < 14.
 *
 * Safari only made `MediaQueryList` an `EventTarget` in 14 (Sept 2020). Before
 * that — including the **iOS 12.5** iPads that are terminal on that release and
 * still in the rural audience #643 exists to support — a `MediaQueryList` has
 * ONLY the deprecated `addListener`/`removeListener` pair.
 *
 * MUI v5+ `useMediaQuery` (`@mui/system/useMediaQuery`) subscribes with
 * `mediaQueryList.addEventListener("change", …)` and has no `addListener`
 * fallback, so on those devices it throws `TypeError: d.addEventListener is not
 * a function` *inside render* (#802). That is not a degraded breakpoint — it
 * unwinds to the Rollbar error boundary and the customer gets AppErrorFallback
 * instead of the page. `Footer` alone puts a `useMediaQuery` call on ~20 routes including
 * `/`, so the whole site is unusable for that cohort.
 *
 * `@vitejs/plugin-legacy`'s `modernPolyfills` cannot cover this: core-js
 * polyfills ECMAScript, not DOM interfaces. And these devices load the MODERN
 * bundle anyway — Safari 12 supports `<script type=module>`, so the nomodule
 * es5 bundle is never fetched. Hence a hand-written prototype patch.
 *
 * Only the `"change"` type is delegated; that is the only event
 * `MediaQueryList` ever fires, and the only one MUI subscribes to.
 *
 * Must run before the first React render — see the call in `src/index.js`.
 */

type LegacyMediaQueryList = MediaQueryList & {
  addListener?: (listener: (event: MediaQueryListEvent) => void) => void;
  removeListener?: (listener: (event: MediaQueryListEvent) => void) => void;
};

export const installMediaQueryListEventShim = (): void => {
  if (
    typeof window === "undefined" ||
    typeof window.matchMedia !== "function"
  ) {
    return;
  }

  // Read the prototype off a real instance rather than `window.MediaQueryList`,
  // which is not exposed on every engine that has `matchMedia`.
  let proto: LegacyMediaQueryList | null = null;
  try {
    proto = Object.getPrototypeOf(
      window.matchMedia("(min-width: 0px)"),
    ) as LegacyMediaQueryList;
  } catch {
    // A `matchMedia` that throws on a valid query is not something we can shim;
    // leaving it alone is strictly better than breaking startup.
    return;
  }

  if (!proto || typeof proto.addEventListener === "function") {
    return; // Safari 14+, and every other modern engine.
  }
  if (
    typeof proto.addListener !== "function" ||
    typeof proto.removeListener !== "function"
  ) {
    return; // Nothing to delegate to.
  }

  proto.addEventListener = function addEventListener(
    this: LegacyMediaQueryList,
    type: string,
    listener: EventListenerOrEventListenerObject,
  ): void {
    if (type !== "change" || typeof listener !== "function") {
      return;
    }
    this.addListener?.(listener as (event: MediaQueryListEvent) => void);
  } as MediaQueryList["addEventListener"];

  proto.removeEventListener = function removeEventListener(
    this: LegacyMediaQueryList,
    type: string,
    listener: EventListenerOrEventListenerObject,
  ): void {
    if (type !== "change" || typeof listener !== "function") {
      return;
    }
    this.removeListener?.(listener as (event: MediaQueryListEvent) => void);
  } as MediaQueryList["removeEventListener"];
};
